import React, { useEffect, useState } from 'react';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  Link,
  Box,
} from '@mui/material';
import { Alert } from '@mui/material';
import moment from 'moment-timezone';
import { IDownload, IFolder, ITick, ITick2 } from '~/_base/components/helixIcons';
import ApiCall from '~/_base/functions/apicalls';
import api from '~/_base/constants/api';
import useSWR from 'swr';
import StepOne from './stepOne';
import StepTwo from './stepTwo';
import StepThree from './stepThree';
import StepFour from './stepFour';
import ConfirmHuman from './confirmHuman';
import getGeoInformation from '../../../_base/functions/getGeoInformation';

require('./animations.css');

const isDevelopment = process.env.NODE_ENV === 'development';

export interface Customer {
  recaptcha_code_v2?: string;
  recaptcha_code_v3?: string;
  email: string;
  organisation_name: string;
  given_name: string;
  surname: string;
  telephone_number: string;
  subdomain: string;
  ip_address: string;
  country_code: string;
  currency_code: string;
  country: string;
  time_zone: string;
}

export default function App() {
  const expanded = useMediaQuery('(min-width:600px)');
  const [formState, setFormState] = useState<Partial<Customer>>({});
  const [showSpinner, setShowSpinner] = useState(false);
  const [step, setStep] = useState(1);
  const [failed, setFailed] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    function getCurrencyCode(countryCode: string, continentCode: string) {
      if (countryCode === 'AU') {
        return 'AUD';
      } else if (countryCode === 'GB') {
        return 'GBP';
      } else if (countryCode === 'NZ') {
        return 'NZD';
      } else if (countryCode === 'ZA') {
        return 'ZAR';
      } else if (continentCode === 'EU' && countryCode !== 'GB') {
        return 'EUR';
      } else {
        return 'USD';
      }
    }
    const resGeo = getGeoInformation('geocode');
    const resIp = getGeoInformation('client_ip');
    if (resGeo && resIp && !isDevelopment) {
      setFormState(prev => ({
        ...prev,
        country_code: resGeo.countryCode,
        country: resGeo.countryName,
        ip_address: resIp.ipAddress,
        currency_code: getCurrencyCode(
          resGeo.countryCode,
          resGeo.continentCode
        ),
      }));
    }
  }, []);

  const { data: badDomains } = useSWR(
    api.badEmailDomains.address(),
    api.badEmailDomains.request
  );

  function handleNext(data: Partial<Customer>) {
    setFormState({ ...formState, ...data });
    setStep(step + 1);
  }

  function handlePrevious() {
    setStep(step - 1);
  }

  function pollForCompletion(address: string) {
    let attempt = 0;
    const pollInterval = 3;
    const timeOut = 600;
    poll();

    async function poll() {
      if (attempt >= timeOut) {
        setFailed(true);
      } else {
        const json: { redirect_to?: string } = await ApiCall(address, 'GET');
        if (json?.redirect_to) {
          const address = json.redirect_to;
          setSuccess(true);
          setTimeout(() => window.location.replace(address), 2500);
        } else {
          attempt = attempt + pollInterval;
          setTimeout(() => {
            poll();
          }, pollInterval * 1000);
        }
      }
    }
  }

  async function handleCreate(customer: Partial<Customer>) {
    setShowSpinner(true);
    const timezone = moment.tz.guess();
    const payload = JSON.stringify({
      ...formState,
      time_zone: timezone ?? 'UTC',
      ...customer,
    });

    try {
      const res = await api.submitTrialSetup.request(payload);
      if (res.ok) {
        const json: { poll: string } = await res.json();
        pollForCompletion(json.poll);
      } else if (res.status === 401) {
        setShowSpinner(false);
        setStep(5);
      } else {
        setFailed(true);
      }
    } catch (err) {
      console.log(err);
      setFailed(true);
    }
  }

  function renderStep() {
    switch (step) {
      case 1:
        return (
          badDomains && (
            <StepOne
              onNext={handleNext}
              formState={formState}
              badDomains={badDomains}
            />
          )
        );
      case 2:
        return (
          <StepTwo
            onNext={handleNext}
            onPrevious={handlePrevious}
            formState={formState}
          />
        );
      case 3:
        return (
          <StepThree
            onNext={handleNext}
            onPrevious={handlePrevious}
            formState={formState}
          />
        );
      case 4:
        return (
          <StepFour
            onCreate={token => handleCreate({ recaptcha_code_v3: token })}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <ConfirmHuman onSubmit={handleCreate} onPrevious={handlePrevious} />
        );
      default:
        return (
          badDomains && (
            <StepOne
              onNext={handleNext}
              formState={formState}
              badDomains={badDomains}
            />
          )
        );
    }
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          pt: 9,
          display: "flex",
          height: "350px",
          maxHeight: expanded ? '20rem' : '10rem',
          minWidth: "100vw",
          backgroundSize: 'cover',
          backgroundImage: 'url(https://www.damstratechnology.com/assets/img/bg-light-desktop.png)'
        }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "950px",
            m: "auto"
          }}>
          <Box sx={{
            p: expanded ? 6 : 3
          }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: expanded ? '65px' : '40px',
                fontWeight: 500
              }}>
              Start free trial
            </Typography>
            <Typography variant="h3" sx={{
              mt: 1
            }}>
              Welcome to Damstra Form's free 30 day trial
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: "950px",
          display: "flex",
          justifyContent: "space-between",
          flexDirection: expanded ? 'row' : 'column',
          m: "auto"
        }}>
        <Box
          sx={{
            width: expanded ? '60%' : '100%',
            p: expanded ? 6 : 3,
            boxSizing: 'border-box'
          }}>
          {failed ? (
            <Alert severity="error">
              <Box sx={{
                mb: 1
              }}>
                There was an error processing your information and we were
                unable to complete it successfully
              </Box>
              <Link href="https://damstra.help.ideagen.com/hc/en-gb/p/support">
                Please contact support to continue.
              </Link>
            </Alert>
          ) : success ? (
            <Box
              sx={{
                p: 0,
                bgcolor: "transparent",
                color: "#191919",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                fontSize: "1.1rem"
              }}>
              <ITick2 sx={{ color: '#b1e41f', mb: '1rem' }} />
              Your site is now ready. Please wait while we redirect you...
            </Box>
          ) : showSpinner ? (
            <React.Fragment>
              <Box className="spinner">
                <Typography variant="h1" sx={{
                  color: "#6f6f6f"
                }}>
                  Sending your information...
                </Typography>
                <Typography
                  variant="h1"
                  sx={{
                    my: 3,
                    mx: 0
                  }}>
                  We're processing your information and creating your trial
                  server...
                </Typography>
                <Box className="bounce1" />
                <Box className="bounce2" />
                <Box className="bounce3" />
              </Box>
            </React.Fragment>
          ) : (
            renderStep()
          )}
        </Box>
        <Box
          sx={{
            bgcolor: "#001e35",
            p: expanded ? 7 : 4,
            width: expanded ? '50%' : '100%',
            boxSizing: 'border-box'
          }}>
          <Typography variant="h4" sx={{
            color: "#fff"
          }}>
            Included in your free 30 day trial
          </Typography>
          <List>
            <ListItem disableGutters>
              <ListItemIcon>
                  <IFolder sx={{ color: '#fff' }}/>
              </ListItemIcon>
              <ListItemText disableTypography>
                <Typography sx={{
                  color: "#fff"
                }}>
                  Unlimited use of example templates
                </Typography>
              </ListItemText>
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon>
                <span style={{ color: '#fff', fontSize: '24px', width: '24px', textAlign: 'center' }}>∞</span>
              </ListItemIcon>
              <ListItemText disableTypography>
                <Typography sx={{
                  color: "#fff"
                }}>
                  Unlimited form creation, data collection and users
                </Typography>
              </ListItemText>
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon>
                  <IDownload sx={{ color: '#fff' }} />
              </ListItemIcon>
              <ListItemText disableTypography>
                <Typography sx={{
                  color: "#fff"
                }}>
                  Access to the Damstra Forms iOS, Android and Windows Apps
                </Typography>
              </ListItemText>
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon>
                  <ITick sx={{ color: '#fff' }}/>
              </ListItemIcon>
              <ListItemText disableTypography>
                <Typography sx={{
                  color: "#fff"
                }}>
                  No credit card required
                </Typography>
              </ListItemText>
            </ListItem>
          </List>
        </Box>
      </Box>
    </React.Fragment>
  );
}

